// @flow
import * as React from 'react';
import {useIntl, defineMessages} from 'react-intl';
import {useAccount} from 'docs/data';
import PropertyList, {PropertyListItem} from 'sail/PropertyList';
import Card from 'sail/Card';
import {Intro} from 'sail/Text';
import Group from 'sail/Group';
import Box from 'sail/Box';
import Button from 'sail/Button';
import {InlineCode} from '../../core/InlineCode/InlineCode';
import {useStripeClui} from '../StripeClui/helpers/react/provider';

const {useState} = React;

const messages = defineMessages({
  getStarted: {
    id: 'docsApp.apiWorksheet.getStarted',
    description: 'Button for beginning a worksheet',
    defaultMessage: 'Get started',
  },
});

type ApiWorksheetProps = {|
  children: Array<Object>,
  worksheetDescription: React.Node,
  successMessage: React.Node,
|};

type StickyData = {
  sticky: string,
  answer: string,
};

export const ApiWorksheet = ({
  children,
  worksheetDescription,
  successMessage,
}: ApiWorksheetProps) => {
  const intl = useIntl();
  const {account} = useAccount();
  const apiKey = account.keys.secret;
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  const [stickyData, setStickyData] = useState([]);
  const [hasStarted, setHasStarted] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);
  const prompts = children;

  const {
    state: {openState},
    open,
  } = useStripeClui();

  const alertUser = (e) => {
    e.preventDefault();
    e.returnValue = 'Your worksheet progress will not be saved';
  };

  React.useEffect(() => {
    window.addEventListener('beforeunload', alertUser);
    return () => {
      window.removeEventListener('beforeunload', alertUser);
    };
  }, []);

  const handleWorksheetStart = () => {
    // Open Stripe Shell when worksheet loads if not already open
    if (openState === 'closed') {
      open();
    }
    setHasStarted(true);
  };

  if (!hasStarted) {
    return (
      <WorksheetCard>
        <Group spacing={8} direction="vertical">
          <Intro>{worksheetDescription}</Intro>
          <Button
            icon="arrowRight"
            iconPosition="right"
            color="primary"
            size="medium"
            label={intl.formatMessage(messages.getStarted)}
            onClick={handleWorksheetStart}
          />
        </Group>
      </WorksheetCard>
    );
  }
  if (hasCompleted) {
    return (
      <WorksheetCard complete>
        <StickyDataList stickyData={stickyData} />
        {successMessage}
      </WorksheetCard>
    );
  }

  // Only append to the stickyData if the prompt's 'sticky' prop isn't empty
  const nextPrompt = (stickyResult) => {
    if (stickyResult.sticky !== '') {
      setStickyData([...stickyData, stickyResult]);
    }
    if (currentPromptIndex === React.Children.count(prompts) - 1) {
      setHasCompleted(true);
    } else {
      setCurrentPromptIndex(currentPromptIndex + 1);
    }
  };

  // Attach the nextPrompt function to all children of type Prompt
  const childrenWithProps = React.Children.map(prompts, (child, index) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {nextPrompt, apiKey, account});
    }
    return child;
  });

  return (
    <WorksheetCard complete={hasCompleted}>
      <StickyDataList stickyData={stickyData} />
      {childrenWithProps[currentPromptIndex]}
    </WorksheetCard>
  );
};

function WorksheetCard(props) {
  return (
    <Card radius="all" shadow="medium" margin={{top: 8, bottom: 8}}>
      <Box
        background={props.complete ? 'green50' : 'blue50'}
        padding={{horizontal: 20, bottom: 20, top: 8}}
      >
        {props.children}
      </Box>
    </Card>
  );
}

type StickyDataListProps = {
  stickyData: Array<StickyData>,
};
function StickyDataList(props: StickyDataListProps) {
  const {stickyData} = props;
  if (stickyData.length === 0) {
    return null;
  } else {
    return (
      <PropertyList
        layout="stacked"
        style={{paddingTop: 8, paddingBottom: 8}}
        divider
      >
        {stickyData.map(function({sticky, answer}) {
          return (
            <PropertyListItem
              key={answer}
              label={sticky}
              definition={<InlineCode copyable>{answer}</InlineCode>}
            />
          );
        })}
      </PropertyList>
    );
  }
}
